import { TableBody, TableHeader } from '@/components/ui'
import { cn } from '@/lib/utils'
import { useLayoutEffect, useRef } from 'react'
import { STICKY_TABLE_CARD, STICKY_TABLE_HEADER } from './sticky-table'

/**
 * Copies the column widths of the scrolling table onto the sticky header
 * table. The widths go straight onto the header's `col` elements, which React
 * does not render, whenever the table resizes or its rows change.
 */
const useSyncedColumns = () => {
  const tableRef = useRef<HTMLTableElement>(null)
  const headerTableRef = useRef<HTMLTableElement>(null)
  const colsRef = useRef<HTMLTableColElement>(null)

  useLayoutEffect(() => {
    const table = tableRef.current
    const headerTable = headerTableRef.current
    const cols = colsRef.current
    if (!table || !headerTable || !cols) return

    const apply = () => {
      // A collapsed row does not report the widths its columns end up with,
      // so measure the first body row that has a cell per column.
      const [header, ...rows] = Array.from(table.rows)
      const row =
        rows.find(r => r.cells.length === header?.cells.length) ?? header
      const widths = Array.from(
        row?.cells ?? [],
        cell => cell.getBoundingClientRect().width
      )
      while (cols.children.length < widths.length)
        cols.appendChild(document.createElement('col'))
      while (cols.children.length > widths.length) cols.lastChild?.remove()
      widths.forEach((width, index) => {
        ;(cols.children[index] as HTMLElement).style.width = `${width}px`
      })
      headerTable.style.width = `${table.getBoundingClientRect().width}px`
    }

    apply()
    const resize = new ResizeObserver(apply)
    resize.observe(table)
    const mutation = new MutationObserver(apply)
    mutation.observe(table, { childList: true, subtree: true })
    return () => {
      resize.disconnect()
      mutation.disconnect()
    }
  }, [])

  return { tableRef, headerTableRef, colsRef }
}

/**
 * A table that scrolls sideways inside its card while its header sticks to
 * the page. A header inside a scroll box can only stick to that box, so the
 * header is drawn twice: collapsed in the scrolling table, where it sizes the
 * columns, and in a sticky copy above it that takes those widths and follows
 * the sideways scroll.
 */
export const ScrollTable: React.FC<{
  header: React.ReactNode
  children: React.ReactNode
  className?: string
}> = ({ header, children, className }) => {
  const { tableRef, headerTableRef, colsRef } = useSyncedColumns()
  const headerScrollRef = useRef<HTMLDivElement>(null)

  return (
    <div className={STICKY_TABLE_CARD}>
      <div
        ref={headerScrollRef}
        className={cn(STICKY_TABLE_HEADER, 'overflow-hidden')}
      >
        <table
          ref={headerTableRef}
          className={cn('table-fixed caption-bottom text-md', className)}
        >
          <colgroup ref={colsRef} />
          <TableHeader className="before:hidden">{header}</TableHeader>
        </table>
      </div>

      <div
        className="overflow-x-auto"
        onScroll={event => {
          if (headerScrollRef.current)
            headerScrollRef.current.scrollLeft = event.currentTarget.scrollLeft
        }}
      >
        <table
          ref={tableRef}
          className={cn('w-full caption-bottom text-md', className)}
        >
          <TableHeader aria-hidden className="before:hidden [&_tr]:collapse">
            {header}
          </TableHeader>
          <TableBody>{children}</TableBody>
        </table>
      </div>
    </div>
  )
}
